<!-- Portal header (all viewports): utility · logo/search/actions · portal nav · account strip -->
<div
  class="custom-header prs-header prs-header--portal"
  [class.prs-header--compact]="deviceType() !== 'desktop'"
  [class.prs-header--nav-hamburger]="useHamburgerNav"
  [class.prs-header--mobile-stack]="deviceType() === 'mobile'"
  [class.sticky-desktop-header]="isSticky()"
  [class.mobile-search-open]="mobileSearchOpen"
  [ngClass]="deviceType()"
  data-testing-id="prs-portal-header"
>
  <!-- Utility bar — hidden on mobile (mock); short labels on tablet -->
  <div class="prs-header-utility">
    <div class="container prs-header-inner">
      <div class="prs-header-utility__row">
        @if (deviceType() === 'desktop' || deviceType() === 'tablet') {
          <span class="prs-header-utility__brand">{{ 'shell.portal.eyebrow' | translate }}</span>
        }
        <div class="prs-header-utility__links">
          <a class="prs-header-utility__link" routerLink="/contact-team" data-testing-id="header-contact-team">
            @if (deviceType() === 'desktop') {
              {{ 'shell.portal.contact_team' | translate }}
            } @else {
              {{ 'shell.portal.contact_team_short' | translate }}
            }
          </a>
          <a class="prs-header-utility__link" routerLink="/help-center" data-testing-id="header-help-center">
            @if (deviceType() === 'desktop') {
              {{ 'shell.portal.help_center' | translate }}
            } @else {
              {{ 'shell.portal.help' | translate }}
            }
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- Main branding / search / actions -->
  <div class="prs-header-top">
    <div class="container prs-header-inner">
      <div class="prs-header-bar d-flex align-items-center">
        <!-- Mobile/tablet, or desktop when portal links would wrap to a second row -->
        @if (showPortalNavHamburger) {
          <button
            class="navbar-toggler prs-header-menu-toggle"
            type="button"
            (click)="toggleAllDrawer()"
            [attr.aria-expanded]="(allDrawerOpen$ | async) || false"
            [attr.aria-label]="'shell.all_menu' | translate"
            data-testing-id="header-menu-toggle"
          >
            <div class="hamburger-menu" [ngClass]="{ open: allDrawerOpen$ | async }" aria-hidden="true">
              <span></span> <span></span> <span></span>
            </div>
          </button>
        }

        <!-- Brand: desktop/tablet side-by-side; mobile stacks PRESIDIO over MyPresidio -->
        <div class="custom-header-logo-container logo-left prs-header-logo prs-header-brand flex-shrink-0">
          <ng-container *ngTemplateOutlet="desktopLogo" />
          <span class="prs-header-brand__divider" aria-hidden="true"></span>
          <span class="prs-header-brand__product">MyPresidio</span>
        </div>

        <!--
          Persistent header search (Baymard). Desktop + tablet: inline in the bar.
          Logged-in omnisearch also shows on account/portal routes.
        -->
        @if (deviceType() !== 'mobile' && (showProductSearch$ | async)) {
          <div
            class="prs-header-search flex-grow-1"
            [attr.data-testing-id]="deviceType() === 'desktop' ? 'prs-header-product-search' : 'prs-header-product-search-tablet'"
          >
            <div class="header-search-container" #searchBoxWrapper>
              <ng-container *ngTemplateOutlet="searchBoxTemplate" />
            </div>
          </div>
        } @else {
          <div class="prs-header-search-spacer flex-grow-1" aria-hidden="true"></div>
        }

        <!-- Actions: mobile mock = notifications + cart only; account lives in strip below -->
        <ul class="user-links prs-header-actions flex-shrink-0 mb-0">
          <li class="prs-header-chrome-item prs-header-notifications">
            <custom-notifications-menu />
          </li>

          <li class="custom-min-box prs-header-chrome-item">
            <custom-mini-basket
              data-testing-id="mini-basket-desktop"
              [view]="deviceType() === 'mobile' ? 'small' : 'full'"
            />
          </li>

          @if (deviceType() !== 'mobile') {
            <li class="prs-header-chrome-item prs-header-account">
              <ng-container *ngIf="isLoggedIn$ | async; else guestHello">
                <custom-account-menu />
              </ng-container>
              <ng-template #guestHello>
                <a
                  routerLink="/login"
                  [queryParams]="{ forcePageView: true }"
                  class="prs-header-account-link"
                  data-testing-id="header-account-menu"
                  rel="nofollow"
                  [attr.aria-label]="'account.login.link' | translate"
                >
                  <i class="bi bi-person-circle prs-header-account-icon" aria-hidden="true"></i>
                  <span class="prs-header-account-name">{{ 'account.login.link' | translate }}</span>
                </a>
              </ng-template>
            </li>
          }
        </ul>
      </div>

      <!-- Mobile: always-visible search under brand bar (mock) -->
      @if (deviceType() === 'mobile' && (showProductSearch$ | async)) {
        <div class="prs-header-search-row" data-testing-id="prs-header-product-search-mobile">
          <div class="header-search-container mobile-search-box mobile-active-search-box" #mobileSearchBoxWrapper>
            <ng-container *ngTemplateOutlet="searchBoxTemplate" />
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Mobile: Viewing account strip sits in accountContextStrip below (no duplicate Account row) -->

  <!--
    FR2.2.1 — Desktop: horizontal portal nav only while it fits one row.
    If links would wrap, keep measuring off-screen and show the All (hamburger) drawer instead.
    Tablet/mobile: hamburger only (no sideways-scroll nav).
  -->
  @if (deviceType() === 'desktop') {
    <nav
      #portalNav
      class="prs-header-portal-nav"
      id="globalNavigation"
      [class.prs-header-portal-nav--measure-only]="useHamburgerNav"
      [attr.aria-hidden]="useHamburgerNav ? 'true' : null"
      [attr.aria-label]="useHamburgerNav ? null : ('shell.portal.nav_aria' | translate)"
      data-testing-id="prs-portal-nav"
    >
      <div class="container prs-header-inner">
        <ul #portalNavList class="prs-header-portal-nav__list">
          <li>
            <a
              class="prs-header-portal-nav__link"
              [routerLink]="getHomePageUrl()"
              routerLinkActive="is-active"
              [routerLinkActiveOptions]="{ exact: true }"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.overview' | translate }}</a
            >
          </li>
          <li *ishIsAuthorizedTo="['APP_B2B_ORDER_APPROVAL', 'APP_B2B_MANAGE_COSTCENTER']">
            <a
              class="prs-header-portal-nav__link prs-header-portal-nav__link--approvals"
              routerLink="/account/requisitions/approver"
              routerLinkActive="is-active"
              data-testing-id="portal-nav-approvals"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              [attr.aria-label]="
                'shell.portal.nav.approvals_aria'
                  | translate: { '0': (pendingApprovalsCount$ | async) || 0 }
              "
            >
              <span>{{ 'shell.portal.nav.approvals' | translate }}</span>
              @if (pendingApprovalsCount$ | async; as pendingApprovals) {
                @if (pendingApprovals > 0) {
                  <span
                    class="prs-header-portal-nav__badge"
                    aria-hidden="true"
                    data-testing-id="portal-nav-approvals-badge"
                    >{{ pendingApprovals > 9 ? '9+' : pendingApprovals }}</span
                  >
                }
              }
            </a>
          </li>
          <li *ngIf="isWarehouseCustomer$ | async">
            <!-- MVP 2.6.3 — My Warehouse held-stock PLP; warehouse accounts only -->
            <a
              class="prs-header-portal-nav__link prs-header-portal-nav__link--warehouse"
              [class.is-active]="warehouseNavActive$ | async"
              routerLink="/category/WH"
              data-testing-id="portal-nav-warehouse"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.warehouse' | translate }}</a
            >
          </li>
          <li>
            <!-- Shop → catalog root CLP (`/c/SHOP`); underline on CLP/PLP/SRP/PDP/cart -->
            <a
              class="prs-header-portal-nav__link"
              [class.is-active]="shopNavActive$ | async"
              [routerLink]="['/c', catalogRootCategoryId]"
              data-testing-id="portal-nav-shop"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.shop' | translate }}</a
            >
          </li>
          <li>
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/quotes"
              routerLinkActive="is-active"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.quotes' | translate }}</a
            >
          </li>
          <!-- Punchout (cXML/OCI): no Orders / Invoices / Contracts / Reports (MVP 2.5.x shopping + quote transfer only) -->
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/orders"
              routerLinkActive="is-active"
              data-testing-id="portal-nav-orders"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.orders' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/invoices"
              routerLinkActive="is-active"
              data-testing-id="portal-nav-invoices"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.invoices' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/contracts"
              [class.is-active]="contractsAssetsNavActive$ | async"
              data-testing-id="portal-nav-contracts"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.contracts' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/reports"
              routerLinkActive="is-active"
              data-testing-id="portal-nav-reports"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.reports' | translate }}</a
            >
          </li>
          <li *ishIsAuthorizedTo="'APP_B2B_MANAGE_USERS'">
            <a
              class="prs-header-portal-nav__link"
              routerLink="/account/organization/users"
              routerLinkActive="is-active"
              data-testing-id="portal-nav-users"
              [attr.tabindex]="useHamburgerNav ? -1 : null"
              >{{ 'shell.portal.nav.users' | translate }}</a
            >
          </li>
        </ul>
      </div>
    </nav>
  }

  <!-- MVP 8.2.1 / 8.2.2 — Viewing account + Co-Browse / Viewing as (mobile: this is the only account row) -->
  <ng-container *ngTemplateOutlet="accountContextStrip" />
</div>

<!-- MVP 8.2.1 / 8.2.2 — Viewing account (left) + Co-Browse / Viewing as (same right slot) -->
<ng-template #accountContextStrip>
  <div class="prs-account-context-row" data-testing-id="header-account-context-row">
    <div class="container prs-header-inner prs-account-context-row__inner">
      <prs-sales-account-banner />
      <div class="prs-account-context-row__cobrowse prs-header-csr-impersonate">
        <prs-impersonation-banner />
        <div *ngIf="canImpersonate$ | async" data-testing-id="header-csr-impersonation-picker">
          <prs-impersonation-picker />
        </div>
      </div>
    </div>
  </div>
</ng-template>

<!-- Common Templates — omnisearch placeholder when feature on; else product-only -->
<ng-template #searchBoxTemplate let-icon="icon">
  <ng-container *ishFeature="'sparqueSearch'">
    <ish-search-box
      data-testing-id="search-box-desktop"
      [deviceType]="deviceType()"
      [configuration]="{
        placeholder: 'search.product.instructional_text' | translate,
        autoSuggest: true,
        maxAutoSuggests: 5,
        icon: icon
      }"
      (closeSearchBox)="onCloseSearchBox()"
      (hideSearchBox)="showSearchBox = false"
      [shouldFocus]="shouldFocusSearch"
    />
  </ng-container>
  <ng-container *ishNotFeature="'sparqueSearch'">
    <custom-lazy-search-box
      data-testing-id="search-box-desktop"
      [configuration]="{
        placeholder: searchPlaceholderKey | translate,
        autoSuggest: true,
        maxAutoSuggests: 10,
        icon: icon
      }"
    />
  </ng-container>
</ng-template>

<ng-template #desktopLogo>
  <a
    rel="home"
    [routerLink]="getHomePageUrl()"
    class="logo"
    data-testing-id="header-home-link-desktop"
    [attr.aria-label]="'common.home.link' | translate"
  ></a>
</ng-template>

<custom-lazy-mini-cart-suggestion />
<custom-shell-fabs />
<custom-all-drawer />
